import { useContext } from 'react';
import { UserContext } from '../components/MiniDrawer';
import { 
  hasPageAccess, 
  hasMenuSectionAccess, 
  hasMenuItemAccess, 
  hasFeatureAccess,
  canManageUsers, 
  canManageProjects, 
  canManageCustomers,
  canViewManagerReports,
  canManageTeams,
  canManageWorklogs,
  canDeleteProjects,
  getRoleDisplayName,
  getUserRoleLevel,
  hasMinimumRole,
  isInRoleGroup,
  getAllRoles,
  isValidRole,
  ROLE_IDS
} from '../utils/roleManager';

export const useRoleAccess = () => {
  const context = useContext(UserContext);
  
  // Handle case where context is not available yet
  if (!context) {
    return {
      currentRole: null,
      userRole: null,
      roleDisplayName: 'Unknown',
      roleLevel: 0,
      isValidRole: false,
      hasPageAccess: () => false,
      hasMenuSectionAccess: () => false,
      hasMenuItemAccess: () => false,
      hasFeatureAccess: () => false,
      canManageUsers: () => false,
      canManageProjects: () => false,
      canManageCustomers: () => false,
      canViewManagerReports: () => false,
      canManageTeams: () => false,
      canManageWorklogs: () => false,
      canDeleteProjects: () => false,
      hasMinimumRole: () => false,
      isInRoleGroup: () => false,
      isAdmin: false,
      isDeliveryManager: false,
      isPeopleManager: false,
      isServiceManager: false,
      isJRServiceManager: false,
      isTeamLeader: false,
      isTeamMember: false,
      canAccessAdminArea: () => false,
      canAccessManagerArea: () => false,
      canAccessUserArea: () => false,
      getAllRoles: () => [],
      isValidRole: () => false,
      ROLE_IDS: {}
    };
  }

  const { backendUser, userRole } = context;
  const currentRole = userRole || backendUser?.role_id;

  return {
    // Current user info
    currentRole,
    userRole: currentRole,
    roleDisplayName: getRoleDisplayName(currentRole),
    roleLevel: getUserRoleLevel(currentRole),
    isValidRole: isValidRole(currentRole),
    
    // Permission checks
    hasPageAccess: (pagePath) => hasPageAccess(currentRole, pagePath),
    hasMenuSectionAccess: (sectionName) => hasMenuSectionAccess(currentRole, sectionName),
    hasMenuItemAccess: (itemName) => hasMenuItemAccess(currentRole, itemName),
    hasFeatureAccess: (featureName) => hasFeatureAccess(currentRole, featureName),
    
    // Specific permission checks
    canManageUsers: () => canManageUsers(currentRole),
    canManageProjects: () => canManageProjects(currentRole),
    canManageCustomers: () => canManageCustomers(currentRole),
    canViewManagerReports: () => canViewManagerReports(currentRole),
    canManageTeams: () => canManageTeams(currentRole),
    canManageWorklogs: () => canManageWorklogs(currentRole),
    canDeleteProjects: () => canDeleteProjects(currentRole),
    
    // Role hierarchy checks
    hasMinimumRole: (minimumRole) => hasMinimumRole(currentRole, minimumRole),
    isInRoleGroup: (groupName) => isInRoleGroup(currentRole, groupName),
    
    // Role constants for easy comparison
    isAdmin: currentRole === ROLE_IDS.ADMIN,
    isDeliveryManager: currentRole === ROLE_IDS.DELIVERY_MANAGER,
    isPeopleManager: currentRole === ROLE_IDS.PEOPLE_MANAGER,
    isServiceManager: currentRole === ROLE_IDS.SERVICE_MANAGER,
    isJRServiceManager: currentRole === ROLE_IDS.JR_SERVICE_MANAGER,
    isTeamLeader: currentRole === ROLE_IDS.TEAM_LEADER,
    isTeamMember: currentRole === ROLE_IDS.TEAM_MEMBER,
    
    // Helper functions
    canAccessAdminArea: () => isInRoleGroup(currentRole, 'ADMIN_AREA'),
    canAccessManagerArea: () => isInRoleGroup(currentRole, 'MANAGERS'),
    canAccessUserArea: () => true, // All authenticated users
    
    // Utility functions
    getAllRoles: () => getAllRoles(),
    isValidRole: (roleId) => isValidRole(roleId),
    
    // Role constants
    ROLE_IDS
  };
};
